import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { topicsApi } from "../../services/api/topicsApi";
import FeedPost from "./FeedPost";
import PostSkeleton from "./PostSkeleton";
import "./TopicPage.css";

const TopicPage = () => {
  const { topic } = useParams();
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [relatedTopics, setRelatedTopics] = useState([]);
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("recent");

  useEffect(() => {
    loadTopicData();
  }, [topic, sortBy]);

  const loadTopicData = async () => {
    setLoading(true);
    try {
      const [postsRes, statsRes, relatedRes] = await Promise.all([
        topicsApi.getTopicFeed(topic, { sortBy }),
        topicsApi.getTopicStats(topic),
        topicsApi.getRelatedTopics(topic),
      ]);

      setPosts(postsRes.data.posts || postsRes.data);
      setStats(statsRes.data.stats || statsRes.data);
      setRelatedTopics(relatedRes.data.topics || relatedRes.data);
      setFollowing(statsRes.data.isFollowing || false);
    } catch (error) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    try {
      if (following) {
        await topicsApi.unfollowTopic(topic);
        setFollowing(false);
      } else {
        await topicsApi.followTopic(topic);
        setFollowing(true);
      }
    } catch (error) {
      alert("Failed to update topic");
    }
  };

  const handlePostUpdate = (postId, updates) => {
    setPosts((prev) =>
      prev.map((post) => (post._id === postId ? { ...post, ...updates } : post))
    );
  };

  const handlePostDelete = (postId) => {
    setPosts((prev) => prev.filter((post) => post._id !== postId));
  };

  return (
    <div className="topic-page">
      <div className="topic-container">
        <header className="topic-header">
          <div className="topic-info">
            <h1>#{topic}</h1>
            {stats && (
              <div className="topic-stats">
                <span>{stats.totalPosts?.toLocaleString() || 0} posts</span>
                <span>{stats.followers?.toLocaleString() || 0} followers</span>
                {stats.postsLast24h > 0 && (
                  <span className="trending">
                    {stats.postsLast24h} posts today
                  </span>
                )}
              </div>
            )}
          </div>

          <button
            className={`follow-btn ${following ? "following" : ""}`}
            onClick={handleFollow}
          >
            {following ? "Following" : "Follow"}
          </button>
        </header>

        {/* Related Topics */}
        {relatedTopics.length > 0 && (
          <div className="related-topics">
            <h3>Related Topics:</h3>
            <div className="topics-list">
              {relatedTopics.map((t) => (
                <a
                  key={t.tag || t}
                  href={`/topics/${t.tag || t}`}
                  className="topic-tag"
                >
                  #{t.tag || t}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Sort Options */}
        <div className="sort-options">
          <button
            className={sortBy === "recent" ? "active" : ""}
            onClick={() => setSortBy("recent")}
          >
            Recent
          </button>
          <button
            className={sortBy === "popular" ? "active" : ""}
            onClick={() => setSortBy("popular")}
          >
            Popular
          </button>
        </div>

        {/* Posts */}
        {loading ? (
          <div className="posts-loading">
            <PostSkeleton />
            <PostSkeleton />
            <PostSkeleton />
          </div>
        ) : posts.length === 0 ? (
          <div className="empty-state">
            <p>No posts found for #{topic}</p>
          </div>
        ) : (
          <div className="posts-list">
            {posts.map((post) => (
              <FeedPost
                key={post._id}
                post={post}
                onUpdate={handlePostUpdate}
                onDelete={handlePostDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TopicPage;
